<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import LoadsTable from './components/LoadsTable'
import { Helper } from '@/mixin/helper'
import Layout from '@/router/layouts/main'
import { mapGetters } from 'vuex'
import Swal from 'sweetalert2'
import { sendEmailVerification } from '@/services/rest-api-identity/lib/verification/verification'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Profile',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    LoadsTable,
  },
  mixins: [Helper],
  data() {
    return {
      remainingSeconds: 0,
      timerInterval: null,
      toastTimestampKey: 'lastVerification',
      lastToastTime: localStorage.getItem('lastVerification'),
      limitOptions: [5, 10, 25, 50, 100],
      imagePreview: require(`@/assets/images/default-image-user.png`),
      maritalStatuses: {
        M: 'Kawin',
        S: 'Belum Kawin',
        D: 'Cerai Hidup',
        X: 'Cerai Mati',
      },
      userProfile: {
        userId: 0,
        fullname: '-',
        gender: '-',
        maritalStatus: '-',
        address: '-',
        email: '-',
        employeeIdentifier: '-',
        phoneNo: '-',
        birthdate: null,
        birthplace: null,
        inactiveCause: null,
        inactiveOn: null,
        language: 'ID',
        driverQualifications: [],
        photograph: null,
        bankAccount: null,
        recruitedAt: null,
        isActive: true,
        isEmployee: false,
        isDriver: true,
        joinOn: null,
        userDocs: [],
        userWorkHistory: [],
        isEmailVerified: null,
        isPhoneVerified: null,
        lastReminder: null,
        position: {
          name: '-',
          departmentName: null,
          structureId: 5,
          unitId: null,
          departmentId: null,
          unitName: null,
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getAsset: 'asset/getAsset',
    }),

    profile() {
      return this.$store.state.access.profile ?? this.userProfile
    },

    isModalCountDownShow() {
      return (
        this.checkVerifyDate &&
        this.remainingSeconds > 0 &&
        (!this.profile.isEmailVerified || !this.profile.isPhoneVerified)
      )
    },

    formatRemainingData() {
      const seconds = Math.max(0, this.remainingSeconds)
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60

      const formattedMinutes = String(minutes).padStart(2, '0')
      const formattedSeconds = String(secs).padStart(2, '0')

      return `${formattedMinutes}:${formattedSeconds}`
    },

    checkVerifyDate() {
      const expiredOn = new Date(this.lastToastTime)
      const now = new Date()
      if (expiredOn < now) {
        return false
      } else {
        return true
      }
    },

    dataIdentity() {
      return [
        {
          key: 'columnDocument',
          label: this.$t('master.form.employee.identity.name'),
          thClass: 'mc-table--50',
          sortable: true,
        },
        {
          key: 'columnNomor',
          label: this.$t('master.form.employee.identity.number'),
          thClass: 'mc-table--50',
          sortable: true,
        },
        {
          key: 'columnExpired',
          label: this.$t('master.form.employee.identity.expired'),
          thClass: 'mc-table--50',
          sortable: true,
        },
        {
          key: 'columnStatus',
          label: this.$t('master.form.fleet.documentPermit.status.text'),
          thClass: 'mc-table--50',
          sortable: true,
        },
      ]
    },

    dataExperience() {
      return [
        {
          key: 'columnCompany',
          label: this.$t('master.form.employee.jobHistory.name'),
          thClass: 'mc-table--50',
          sortable: true,
        },
        {
          key: 'columnPosition',
          label: this.$t('master.form.employee.jobHistory.position'),
          thClass: 'mc-table--50',
          sortable: true,
        },
        {
          key: 'columnEntryDate',
          label: this.$t('master.form.employee.jobHistory.entryDate'),
          thClass: 'mc-table--50',
          sortable: true,
        },
        {
          key: 'columnExitDate',
          label: this.$t('master.form.employee.jobHistory.exitDate'),
          thClass: 'mc-table--50',
          sortable: true,
        },
      ]
    },

    personalInformationData() {
      return [
        {
          label: this.$t('profile.content.personalInformation.name'),
          value: this.profile.fullname ?? '-',
        },
        {
          label: this.$t('profile.content.personalInformation.employeeId'),
          value: this.profile.employeeIdentifier ?? '-',
        },
        {
          label: this.$t('profile.content.personalInformation.placeDateOfBirth'),
          value: `${this.profile.birthplace ?? '-'}, ${
            this.profile.birthdate ? this.momentDateShortMonth(this.profile.birthdate) : '-'
          }`,
        },
        {
          label: this.$t('profile.content.personalInformation.gender'),
          value:
            this.profile.gender === 'M'
              ? this.$t('global.object.male')
              : this.$t('global.object.female'),
        },
        {
          label: this.$t('profile.content.personalInformation.maritalStatus'),
          value: this.maritalStatuses[this.profile.maritalStatus] ?? '-',
        },
        {
          label: this.$t('profile.content.personalInformation.address'),
          value: this.profile.address ?? '-',
        },
      ]
    },

    jobInformationData() {
      return [
        {
          label: this.$t('profile.content.jobInformation.position'),
          value: this.profile.position?.name ?? '-',
        },
        {
          label: this.$t('profile.content.jobInformation.workUnit'),
          value: this.profile.position?.unitName ?? '-',
        },
        {
          label: this.$t('profile.content.jobInformation.dateOfJoining'),
          value: this.profile.recruitedAt
            ? this.momentDateShortMonth(this.profile.recruitedAt)
            : '-',
        },
        {
          label: this.$t('profile.content.jobInformation.department'),
          value: this.profile.position?.departmentName ?? '-',
        },
        {
          label: this.$t('profile.content.jobInformation.workStatus'),
          value: 'workStatus',
        },
        {
          label: this.$t('profile.content.jobInformation.assignmentPlace'),
          value: this.profile.position?.departmentName ?? '-',
        },
      ]
    },

    title() {
      return this.$t('profile.title')
    },
  },

  watch: {
    lastToastTime: {
      handler() {
        this.startCountdown()
      },
      immediate: true,
    },
  },

  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
  },

  methods: {
    startCountdown() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
      }
      if (this.lastToastTime) {
        const now = new Date().getTime()
        const expirationDate = new Date(this.lastToastTime).getTime()
        const diffInSeconds = Math.max(0, Math.floor((expirationDate - now) / 1000))
        this.remainingSeconds = diffInSeconds

        this.timerInterval = setInterval(() => {
          if (this.remainingSeconds > 0) {
            this.remainingSeconds--
          } else {
            clearInterval(this.timerInterval)
          }
        }, 1000)
      }
    },

    async toSubmitVerification(type) {
      const now = new Date()
      const currentTime = now.getTime()

      if (this.lastToastTime && currentTime - parseInt(this.lastToastTime, 10) < 60000) {
        this.$mcToast.warning(this.$t('profile.alert.tryAgain'))
        return
      }
      Swal.fire({
        title: this.$t('profile.alert.text.cancel'),
        text: this.$t('profile.alert.text.confirmCancel'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('profile.action.yes'),
        cancelButtonText: this.$t('profile.action.no'),
      }).then(async (data) => {
        try {
          if (!data.isConfirmed) {
            Swal.fire({
              title: this.$t('profile.alert.failed.title'),
              text: this.$t('profile.alert.failed.text'),
              icon: 'error',
            })
            return
          }
          const INTERNAL_USER_API =
            process.env.VUE_APP_ENV === 'production'
              ? process.env.VUE_APP_INTERNAL_USER_PROD_API
              : process.env.VUE_APP_INTERNAL_USER_API

          const res = await sendEmailVerification({
            resource_url: `${INTERNAL_USER_API}${this.profile.userId}`,
            verification_type: type,
            email: this.profile.email,
            phone_no: this.profile.phoneNo,
          })
          if (res?.message === 'Data Duplicate') {
            Swal.fire({
              title: this.$t('profile.alert.failed.title'),
              text: this.$t('profile.alert.failed.text'),
              icon: 'error',
            })
            return
          }
          Swal.fire({
            title: this.$t('profile.alert.success.title'),
            text: this.$t('profile.alert.success.text'),
            icon: 'success',
          })
          now.setMinutes(now.getMinutes() + 1)
          localStorage.setItem(this.toastTimestampKey, now)
          this.lastToastTime = now
        } catch (err) {
          Swal.fire({
            title: this.$t('profile.alert.failed.title'),
            text: this.$t('profile.alert.failed.text'),
            icon: 'error',
          })
        }
      })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <b-row>
      <!-- Left Column -->
      <b-col lg="4">
        <!-- Login Information Card -->
        <b-card class="mb-4">
          <b-row>
            <b-col lg="12" class="d-flex justify-content-center">
              <div class="text-center">
                <img
                  :src="getUserPhoto()"
                  alt
                  class="avatar-lg rounded-circle img-thumbnail mb-2"
                  style="object-fit: cover"
                />
                <div class="align-content-center">
                  <h5 class="mb-2">
                    {{ this.profile.fullname ?? '-' }}
                  </h5>
                  <p class="text-muted mb-1 font-size-12">
                    {{ this.profile.position?.name ?? '-' }}
                  </p>
                  <span
                    class="mc-profile-status-badge font-size-10"
                    :class="
                      this.profile.isActive
                        ? 'mc-profile-status-active'
                        : 'mc-profile-status-inactive'
                    "
                  >
                    {{ this.profile.isActive ? $t('global.active') : $t('global.nonActive') }}
                  </span>
                </div>
              </div>
            </b-col>
          </b-row>

          <div class="mc-profile-hr my-3"></div>

          <h6 class="mb-3">
            {{ $t('profile.content.loginInformation.title') }}
          </h6>

          <b-row class="border rounded-3 m-0 p-1 mb-3">
            <b-col xl="8" lg="12" md="12" sm="12" class="p-1">
              <div class="d-flex gap-1">
                <i class="icon-outline_email font-size-16 align-middle me-1"></i>
                <div>
                  <div class="small">Email</div>
                  <div v-if="profile.email" class="small text-muted">
                    {{ this.profile.email ?? '-' }}
                    <span v-if="profile?.isEmailVerified" class="mc-profile-verified">
                      <i class="icon-fill_check-seal font-size-16 align-middle me-1"></i>
                    </span>
                    <span v-else class="mc-profile-unverified">
                      <i
                        class="icon-fill_alert-triangle font-size-16 align-middle me-1 mc-profile-color-warning"
                      ></i>
                    </span>
                  </div>
                  <b-badge v-else class="bg-warning">
                    <i
                      class="icon-fill_alert-triangle font-size-16 align-middle me-1 mc-profile-banner__icon"
                    ></i>
                    {{ $t('profile.content.personalInformation.notAdded') }}
                  </b-badge>
                </div>
              </div>
            </b-col>
            <b-col xl="4" lg="12" md="12" sm="12" class="p-1">
              <div
                v-if="profile.email"
                class="h-100 d-flex flex-xl-row flex-column align-items-center justify-content-end gap-2"
              >
                <b-button
                  v-if="!profile.isEmailVerified"
                  :disabled="remainingSeconds > 0"
                  variant="primary"
                  class="small w-100 p-1"
                  size="sm"
                  @click="() => toSubmitVerification('email')"
                >
                  <span class="small">{{ $t('forgotPassword.action.verification') }}</span>
                  <i
                    class="mc-profile-banner__icon icon-fill_checkmark-circle-2 font-size-14 align-middle"
                    style="color: #ffffff !important"
                  ></i>
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mc-profile-w-xl-100 p-1"
                  @click="
                    () => {
                      this.$router.push({ name: 'Change Email' })
                    }
                  "
                >
                  <i class="icon-outline_edit font-size-14 align-middle"></i>
                  <span class="mc-profile-button-text small">{{
                    $t('profile.content.loginInformation.edit')
                  }}</span>
                </b-button>
              </div>
              <div
                v-else
                class="h-100 d-flex flex-xl-row flex-column align-items-center justify-content-end gap-2"
              >
                <b-button
                  variant="primary"
                  size="sm"
                  class="mc-profile-w-xl-100 p-1"
                  @click="
                    () => {
                      this.$router.push({ name: 'Add Email' })
                    }
                  "
                >
                  <i class="icon-fill_plus mc-profile-banner__icon font-size-14 align-middle"></i>
                  <span class="mc-profile-button-text small">{{
                    $t('profile.content.loginInformation.add')
                  }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row>

          <b-row class="border rounded-3 m-0 p-1 mb-3">
            <b-col xl="8" lg="12" md="12" sm="12" class="p-1">
              <div class="d-flex gap-1">
                <i class="icon-outline_whatsapp font-size-16 align-middle me-1"></i>
                <div>
                  <div class="small">{{ $t('profile.content.loginInformation.phoneNumber') }}</div>
                  <div v-if="profile.phoneNo" class="small text-muted">
                    {{ this.profile.phoneNo ?? '-' }}
                    <span v-if="profile?.isPhoneVerified" class="mc-profile-verified">
                      <i class="icon-fill_check-seal font-size-16 align-middle me-1"></i>
                    </span>
                    <span v-else class="mc-profile-unverified">
                      <i
                        class="icon-fill_alert-triangle font-size-16 align-middle me-1 mc-profile-color-warning"
                      ></i>
                    </span>
                  </div>
                  <b-badge v-else class="bg-warning">
                    <i
                      class="icon-fill_alert-triangle font-size-16 align-middle me-1 mc-profile-banner__icon"
                    ></i>
                    {{ $t('profile.content.personalInformation.notAdded') }}
                  </b-badge>
                </div>
              </div>
            </b-col>
            <b-col xl="4" lg="12" md="12" sm="12" class="p-1">
              <div
                v-if="profile.phoneNo"
                class="h-100 d-flex flex-xl-row flex-column align-items-center justify-content-end gap-2"
              >
                <b-button
                  v-if="!profile.isPhoneVerified"
                  :disabled="remainingSeconds > 0"
                  variant="primary"
                  class="small w-100 p-1"
                  size="sm"
                  @click="() => toSubmitVerification('phone')"
                >
                  <span class="small">{{ $t('forgotPassword.action.verification') }}</span>
                  <i
                    class="mc-profile-banner__icon icon-fill_checkmark-circle-2 font-size-14 align-middle"
                    style="color: #ffffff !important"
                  ></i>
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mc-profile-w-xl-100 p-1"
                  @click="
                    () => {
                      this.$router.push({ name: 'Change Number' })
                    }
                  "
                >
                  <i class="icon-outline_edit font-size-14 align-middle"></i>
                  <span class="mc-profile-button-text small">{{
                    $t('profile.content.loginInformation.edit')
                  }}</span>
                </b-button>
              </div>
              <div
                v-else
                class="h-100 d-flex flex-xl-row flex-column align-items-center justify-content-end gap-2"
              >
                <b-button
                  variant="primary"
                  size="sm"
                  class="mc-profile-w-xl-100 p-1"
                  @click="
                    () => {
                      this.$router.push({ name: 'Add Number' })
                    }
                  "
                >
                  <i class="icon-fill_plus mc-profile-banner__icon font-size-14 align-middle"></i>
                  <span class="mc-profile-button-text small">{{
                    $t('profile.content.loginInformation.add')
                  }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row>
          <div
            v-if="this.isModalCountDownShow"
            class="border rounded-3 m-0 p-2 mb-3 d-flex gap-1 mc-profile-border-waiting-verify"
          >
            <i
              class="icon-outline_alert-triangle font-size-16 align-middle me-1 mc-profile-color-warning"
            ></i>
            <div>
              <div class="small">{{ $t('profile.alert.verificationPending.title') }}</div>
              <div v-if="profile.phoneNo" class="small text-muted">
                {{ $t('profile.alert.verificationPending.text') }}
                <span class="mc-profile-text-blue"> {{ formatRemainingData }} </span>
              </div>
            </div>
          </div>

          <!-- Temporary Comment - Need Fix Concept -->
          <!-- <b-row class="border rounded-3 m-0 p-1">
            <b-col xl="8" lg="12" md="12" sm="12" class="p-1">
              <div class="d-flex gap-1">
                <i class="icon-outline_lock font-size-16 align-middle me-1"></i>
                <div>
                  <div class="small">Kata Sandi</div>
                  <div class="small text-muted">
                    terakhir diubah {{ this.profile.passwordUpdatedAt ?? '~' }}
                  </div>
                </div>
              </div>
            </b-col>
            <b-col xl="4" lg="12" md="12" sm="12" class="p-1">
              <div
                class="h-100 d-flex flex-xl-row flex-column align-items-center justify-content-end gap-2"
              >
                <b-button variant="outline-primary" class="mc-profile-w-xl-100 p-1" size="sm">
                  <i class="icon-outline_edit font-size-14 align-middle"></i>
                  <span class="mc-profile-button-text small">{{
                    $t('profile.content.loginInformation.edit')
                  }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row> -->
        </b-card>
      </b-col>

      <!-- Right Column -->
      <b-col lg="8">
        <!-- Work Information Card -->
        <b-card class="mb-4">
          <h6 class="mb-3">
            {{ $t('profile.content.personalInformation.title') }}
          </h6>
          <b-row class="border rounded-3 m-0 mb-3 p-3 gap-2">
            <b-col v-for="(data, i) in this.personalInformationData" :key="i" xl="12">
              <b-row class="m-0 p-0">
                <b-col xl="5" lg="5" md="5" sm="5" class="p-0">
                  <p class="text-muted mb-0 font-size-12">{{ data.label }}</p>
                </b-col>
                <b-col xl="7" lg="7" md="7" sm="7" class="p-0">
                  <h6 class="mb-0 font-size-12">{{ data.value }}</h6>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <br />

          <h6 class="mb-3">
            {{ $t('profile.content.jobInformation.title') }}
          </h6>
          <b-row class="border rounded-3 m-0 p-3 gap-2">
            <b-col v-for="(data, i) in this.jobInformationData" :key="i" xl="12">
              <b-row class="m-0 p-0">
                <b-col xl="5" lg="5" md="5" sm="5" class="p-0">
                  <p class="text-muted mb-0 font-size-12">{{ data.label }}</p>
                </b-col>
                <b-col xl="7" lg="7" md="7" sm="7" class="p-0">
                  <h6 v-if="data.value === 'workStatus'" class="mb-0 font-size-12 d-flex">
                    <span v-if="!profile.isActive" class="mc-status mc-status--grey">
                      {{ $t('global.nonActive') }}
                    </span>
                    <span v-else class="mc-status mc-status--green">
                      {{ $t('global.active') }}
                    </span>
                  </h6>
                  <h6 v-else class="mb-0 font-size-12">{{ data.value }}</h6>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <br />

          <h5 class="my-3">
            {{ $t('master.form.employee.jobHistory.title') }}
          </h5>

          <LoadsTable
            :items="profile.userWorkHistory"
            :fields="dataExperience"
            :limitOptions="limitOptions"
          />

          <br />

          <h5 class="my-3">
            {{ $t('profile.content.ohiIdentity.title') }}
          </h5>

          <LoadsTable
            :items="profile.userDocs"
            :fields="dataIdentity"
            :limitOptions="limitOptions"
          />
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>
